let mermaidSequence = 0

const mathExpression = /(\\\[[\s\S]*?\\\]|\$\$[\s\S]*?\$\$|\\\([\s\S]*?\\\)|(?<!\\)\$(?!\$)(?:\\.|[^$\\\n])+\$(?!\$))/g

function isMathIgnored(node: Text): boolean {
  return Boolean(node.parentElement?.closest('pre, code, script, style, svg, .katex, .mermaid-diagram'))
}

function formulaParts(token: string) {
  if (token.startsWith('$$')) return { expression: token.slice(2, -2).trim(), display: true }
  if (token.startsWith('\\[')) return { expression: token.slice(2, -2).trim(), display: true }
  if (token.startsWith('\\(')) return { expression: token.slice(2, -2).trim(), display: false }
  return { expression: token.slice(1, -1).trim(), display: false }
}

async function renderMath(root: HTMLElement) {
  const source = root.textContent || ''
  if (!source.includes('$') && !source.includes('\\(') && !source.includes('\\[')) return

  const [{ default: katex }] = await Promise.all([
    import('katex'),
    import('katex/dist/katex.min.css')
  ])
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT)
  const nodes: Text[] = []
  while (walker.nextNode()) {
    const node = walker.currentNode as Text
    if (!isMathIgnored(node) && mathExpression.test(node.data)) nodes.push(node)
    mathExpression.lastIndex = 0
  }

  for (const node of nodes) {
    const value = node.data
    const fragment = document.createDocumentFragment()
    let cursor = 0
    mathExpression.lastIndex = 0
    for (const match of value.matchAll(mathExpression)) {
      const offset = match.index || 0
      if (offset > cursor) fragment.append(document.createTextNode(value.slice(cursor, offset)))
      const { expression, display } = formulaParts(match[0])
      if (!expression) {
        fragment.append(document.createTextNode(match[0]))
      } else {
        // Keep valid markup even when a display formula appears inside a
        // paragraph produced by Goldmark. CSS turns this span into a block.
        const wrapper = document.createElement('span')
        wrapper.className = display ? 'math-display' : 'math-inline'
        // KaTeX produces the markup from the formula itself; original text is
        // never inserted as HTML.
        wrapper.innerHTML = katex.renderToString(expression, {
          displayMode: display,
          throwOnError: false,
          strict: 'ignore',
          trust: false
        })
        fragment.append(wrapper)
      }
      cursor = offset + match[0].length
    }
    if (cursor < value.length) fragment.append(document.createTextNode(value.slice(cursor)))
    node.replaceWith(fragment)
  }
}

async function renderMermaid(root: HTMLElement) {
  const blocks = [...root.querySelectorAll<HTMLElement>('.code-block[data-language="mermaid"]')]
  if (!blocks.length) return

  const { default: mermaid } = await import('mermaid')
  mermaid.initialize({
    startOnLoad: false,
    securityLevel: 'strict',
    theme: 'dark',
    fontFamily: 'PingFang SC, Hiragino Sans GB, Microsoft YaHei, sans-serif',
    themeVariables: {
      background: '#0e1728',
      primaryColor: '#172b45',
      primaryTextColor: '#d9e5f4',
      primaryBorderColor: '#3b82a6',
      lineColor: '#7dd3fc',
      tertiaryColor: '#132238'
    }
  })

  for (const block of blocks) {
    const source = block.querySelector('pre code')?.textContent?.trim()
    if (!source) continue
    try {
      const id = `goblog-mermaid-${mermaidSequence++}`
      const { svg, bindFunctions } = await mermaid.render(id, source)
      const diagram = document.createElement('div')
      diagram.className = 'mermaid-diagram'
      diagram.setAttribute('role', 'img')
      diagram.setAttribute('aria-label', 'Mermaid 图表')
      diagram.innerHTML = svg
      block.replaceWith(diagram)
      bindFunctions?.(diagram)
    } catch {
      // Preserve the source code block when Mermaid rejects invalid syntax.
      block.classList.add('mermaid-render-failed')
    }
  }
}

// Enhances the trusted HTML generated by the backend after Vue has mounted it.
// Both dependencies are loaded only for articles that actually use them.
export async function enhanceArticleContent(root: HTMLElement | null) {
  if (!root) return
  await Promise.all([renderMath(root), renderMermaid(root)])
}
